import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  NonUSFProductViewModel,
  PIMLevelEnum,
  PIMSelectionViewModel,
  vendorDropdownEvent,
} from '@inventory/pages/manage-non-usf-products/models/non-usf-products-view-model';
import { IonNav } from '@ionic/angular';
import { PlatformEnum } from '@panamax/app-state';
import { perUnit } from '@shared/constants/inventory-constants';
import { ExternalVendorState } from '@usf/ngrx-product';
import { HierarchyDto, PIMCategory, PIMClass } from '@usf/product-types';
import {
  combineLatest,
  firstValueFrom,
  Observable,
  startWith,
  Subscription,
} from 'rxjs';
import { PimSelectionModalComponent } from '../pim-selection-modal/pim-selection-modal.component';
import { IngredientUnitsCalculations } from '@app/mpp/model/recipe-detail-view-model';
import { UnitsDropDownEvent } from '@app/mpp/model/ingredient-card-click-event';
import { UnitOfMeasure } from '@usf/mpp-types';
import { UnitConversionHelper } from '@app/mpp/shared/helper/unit-conversion-helper';
import { UnitOfMeasureLabels } from '@app/mpp/shared/enum/unit-conversion-enums';

@Component({
  selector: 'app-add-non-usf-product',
  templateUrl: './add-non-usf-product.component.html',
  styleUrls: ['./add-non-usf-product.component.scss'],
})
export class AddNonUsfProductComponent implements OnInit {
  @Input() usfProductForm: FormGroup;
  @Input() vendors: ExternalVendorState[];
  @Input() pimData: PIMClass[];
  @Input() pimClassHeirachy: HierarchyDto;
  @Input() pimCategoryHeirarchy: HierarchyDto;
  @Input() pimGroupHeirarchy: HierarchyDto;
  @Input() platform: PlatformEnum;
  @Input() isAndroidTouch: boolean;
  @Input() isEditMode = false;
  @Input() originalProduct: NonUSFProductViewModel;
  @Input() pimFlag;
  @Input() mppMeasurementFlag: boolean;
  @Input() measurements: UnitOfMeasure;
  @Input() mppCalculations$: Observable<IngredientUnitsCalculations>;
  @Output() dismisEvent: Function;
  @Output() submitEvent: Function;
  @Output() vendorsEvent: Function;
  @Output() unitsEvent: Function;
  @Output() priceOptionEvent: Function;
  @Output() pimCodeEvent: Function;
  @Output() mppInfoIconEvent: Function;
  @Output() mppDropdownEvent: Function;
  @Output() calculationEvent: Function;
  @Output() sendMeasurementsBack: Function;

  readonly PlatformEnum = PlatformEnum;
  readonly PIMLevelEnum = PIMLevelEnum;

  inFocus = false;
  valuesDiffer = false;
  formSub$: Subscription;
  pimClassOptions: PIMClass[] = [];
  pimCategoryOptions: PIMCategory[] = [];
  pimGroupOptions: HierarchyDto[] = [];

  constructor(private ionNav: IonNav) {}

  async ngOnInit(): Promise<void> {
    if (this.usfProductForm && this.isEditMode) {
      this.formSub$ = combineLatest([
        this.usfProductForm.valueChanges.pipe(startWith(undefined)),
        this.mppCalculations$,
      ]).subscribe(([form, calculations]) => {
        this.checkSubmit(calculations);
      });
    } else {
      this.valuesDiffer = true;
    }
    if (this.pimFlag) {
      this.setupPimData();
    }
  }

  setupPimData = () => {
    this.pimClassOptions = this.pimData;
    if (this.pimClassHeirachy) {
      this.pimCategoryOptions =
        this.pimClassOptions.find(
          item => item.pimClass.code === this.pimClassHeirachy.code,
        )?.pimCategories ?? [];
      if (this.pimCategoryHeirarchy) {
        this.pimGroupOptions =
          this.pimCategoryOptions.find(
            item => item.pimCategory.code === this.pimCategoryHeirarchy.code,
          )?.pimGroups ?? [];
      }
    }
  };

  dismiss = () => {
    this.cleanup();
    this.dismisEvent(false, this.vendors, this.isEditMode);
  };

  submit = () => {
    this.cleanup();
    this.submitEvent(this.isEditMode);
  };

  onVendorClick = (event: Event) => {
    this.vendorsEvent({ vendors: this.vendors, event });
  };

  onPriceOptionClick = (event: Event) => {
    this.priceOptionEvent(event);
  };

  onUnitsClick = (event: Event) => {
    this.unitsEvent(event);
  };

  inputFocus = () => {
    this.inFocus = true;
  };

  inputBlur = () => {
    this.inFocus = false;
  };

  weightUpdate = () => {
    this.inputBlur();
    setTimeout(() => {
      let weight = Number.parseFloat(
        this.usfProductForm.get('avgNetWeight').value,
      );
      if (!!weight) {
        this.measurements = UnitConversionHelper.convertAndUpdateSolidMeasures(
          weight,
          UnitOfMeasureLabels.POUND,
        );
      }

      this.sendMeasurementsBack(this.measurements);
    }, 100);
  };

  checkSubmit = (currentCalculation?: IngredientUnitsCalculations) => {
    const nameCheck =
      this.usfProductForm.get('name').value !==
      this.originalProduct.productDescLong;
    const brandCheck =
      this.usfProductForm.get('brand').value !== this.originalProduct.brand;
    const manuCheck =
      this.usfProductForm.get('manufacturerNum').value !==
      this.originalProduct.manufacturerProductNumber;
    const packCheck =
      this.usfProductForm.get('packSize').value !==
      this.originalProduct.salesPackSize;
    const netWeightCheck =
      Number(this.usfProductForm.get('avgNetWeight').value) !==
      Number(this.originalProduct.averageNetWeight ?? 0);
    const priceCheck =
      Number(this.usfProductForm.get('price').value) !==
      Number(this.originalProduct.price ?? 0);
    const priceUOMCheck =
      this.usfProductForm.get('units').value !== this.originalProduct.priceUom;
    const pricePerUnit =
      (this.usfProductForm.get('priceOption').value === perUnit) !==
      this.originalProduct.isPricePerUnitInd;
    const unitPerCaseCheck =
      Number(this.usfProductForm.get('unitsPerCase').value) !==
      Number(this.originalProduct.unitsPerCase ?? 0);
    const vendorCheck =
      this.usfProductForm.get('vendor').value?.vendorId !==
      this.originalProduct.vendorId;
    const productYieldCheck =
      Number(this.usfProductForm.get('productYield').value) !==
      Number(this.originalProduct.productYield ?? 0);

    // need net weight to even have a valid calculation, check calculations is filled out
    let calculationsValid = false;

    if (
      !!Number(this.usfProductForm.get('avgNetWeight').value) &&
      !!currentCalculation
    ) {
      calculationsValid =
        !!currentCalculation?.baseQuantity &&
        !!currentCalculation?.baseUnit &&
        !!currentCalculation?.conversionQunatity &&
        !!currentCalculation?.conversionUnit;
    }

    let pimCheck = this.pimFlag;
    // Need to check if values are different.
    // Also need to mmake sure it's not empty string and undefined, which are the same for this system.
    if (this.pimFlag) {
      const undefinedToEmptyCheckClass =
        this.usfProductForm.get('pimClass').value === undefined &&
        this.originalProduct.pimClass === '';
      const classCheck =
        this.usfProductForm.get('pimClass').value !==
        this.originalProduct.pimClass;
      const undefinedToEmptyCheckCategory =
        this.usfProductForm.get('pimCategory').value === undefined &&
        this.originalProduct.category === '';
      const categoryCheck =
        this.usfProductForm.get('pimCategory').value !==
        this.originalProduct.category;
      const undefinedToEmptyCheckGroup =
        this.usfProductForm.get('pimGroup').value === undefined &&
        this.originalProduct.groupId === '';
      const groupCheck =
        this.usfProductForm.get('pimGroup').value !==
        this.originalProduct.groupId;
      pimCheck =
        (classCheck && !undefinedToEmptyCheckClass) ||
        (categoryCheck && !undefinedToEmptyCheckCategory) ||
        (groupCheck && !undefinedToEmptyCheckGroup);
    }

    this.valuesDiffer =
      nameCheck ||
      brandCheck ||
      manuCheck ||
      packCheck ||
      netWeightCheck ||
      priceCheck ||
      priceUOMCheck ||
      pricePerUnit ||
      unitPerCaseCheck ||
      vendorCheck ||
      productYieldCheck ||
      pimCheck ||
      calculationsValid;
  };

  cleanup = () => {
    if (this.formSub$) this.formSub$.unsubscribe();
  };

  goToPimSelection = (level: PIMLevelEnum) => {
    let data: PIMSelectionViewModel = {
      platform: this.platform,
      level,
      heirarchyData: [],
      callbackSaveChange: this.savePIMSelectionMade,
      dismissFunction: this.dismiss,
      searchPlaceholder: '',
      title: '',
      initialSelection: undefined,
    };
    switch (level) {
      case PIMLevelEnum.class:
        data.title = 'i18n.nonUsfProducts.pimClassChoose';
        data.searchPlaceholder = 'i18n.nonUsfProducts.pimClassSearch';
        data.heirarchyData = this.pimClassOptions.map(item => {
          return item.pimClass;
        });
        data.initialSelection = this.pimClassHeirachy;
        break;
      case PIMLevelEnum.category:
        data.title = 'i18n.nonUsfProducts.pimCategoryChoose';
        data.searchPlaceholder = 'i18n.nonUsfProducts.pimCategorySearch';
        data.heirarchyData = this.pimCategoryOptions.map(item => {
          return item.pimCategory;
        });
        data.initialSelection = this.pimCategoryHeirarchy;
        break;
      case PIMLevelEnum.group:
        data.title = 'i18n.nonUsfProducts.pimGroupChoose';
        data.searchPlaceholder = 'i18n.nonUsfProducts.pimGroupSearch';
        data.heirarchyData = this.pimGroupOptions;
        data.initialSelection = this.pimGroupHeirarchy;
        break;
    }

    this.ionNav.push(PimSelectionModalComponent, data);
  };

  savePIMSelectionMade = (level: PIMLevelEnum, selectionData: HierarchyDto) => {
    switch (level) {
      case PIMLevelEnum.class:
        this.pimClassHeirachy = selectionData;
        if (selectionData === undefined) {
          this.pimCategoryOptions = [];
        } else {
          this.pimCategoryOptions =
            this.pimClassOptions.find(
              item => item.pimClass.code === selectionData.code,
            )?.pimCategories ?? [];
        }
        this.pimGroupOptions = [];
        this.pimCategoryHeirarchy = undefined;
        this.pimGroupHeirarchy = undefined;

        this.pimCodeEvent(PIMLevelEnum.class, selectionData?.code);
        this.pimCodeEvent(PIMLevelEnum.category, undefined);
        this.pimCodeEvent(PIMLevelEnum.group, undefined);
        break;
      case PIMLevelEnum.category:
        this.pimCategoryHeirarchy = selectionData;
        if (selectionData === undefined) {
          this.pimGroupOptions = [];
        } else {
          this.pimGroupOptions =
            this.pimCategoryOptions.find(
              item => item.pimCategory.code === selectionData.code,
            )?.pimGroups ?? [];
        }
        this.pimGroupHeirarchy = undefined;
        this.pimCodeEvent(PIMLevelEnum.category, selectionData?.code);
        this.pimCodeEvent(PIMLevelEnum.group, undefined);
        break;
      case PIMLevelEnum.group:
        this.pimGroupHeirarchy = selectionData;
        this.pimCodeEvent(PIMLevelEnum.group, selectionData?.code);
        break;
    }
  };

  onMppBaseQuantityChange = (value: number) => {
    this.calculationEvent(value, true);
  };

  onMppConversionQuantityChange = (value: number) => {
    this.calculationEvent(value, false);
  };

  onMppBaseUnitClick = (event: UnitsDropDownEvent) => {
    this.mppDropdownEvent(event, true);
  };

  onMppConversionUnitClick = (event: UnitsDropDownEvent) => {
    this.mppDropdownEvent(event, false);
  };

  onMppInfoIconClick = (event: Event) => {
    if (this.mppInfoIconEvent) {
      this.mppInfoIconEvent(event);
    }
  };
}
